<script setup lang="ts">
import { useDebounceFn } from '@vueuse/core'
import { useAdminListLinkableSalesDemos } from '@swipe4work/api-orval'
import type { AdminListLinkableSalesDemosParams, ListLinkableSalesDemosLinkableDemoDto } from '@swipe4work/models-orval'
import { getStatusLabel, getStatusSeverity } from '@/features/sales/composables/useSalesMetricsAdapter'

const props = withDefaults(defineProps<{
  modelValue?: number | null
  inputId?: string
  invalid?: boolean
  showClear?: boolean
  placeholder?: string
  disabled?: boolean
}>(), {
  modelValue: null,
  showClear: true,
  placeholder: 'Selecteer een demo zonder bedrijf',
  invalid: false,
  disabled: false,
})

const emit = defineEmits<{
  (e: 'update:modelValue', v: number | null): void
}>()

/*
 * The endpoint caps its result, so the typed term also goes back into the query: the server widens
 * the candidate pool beyond that cap while PrimeVue filters the loaded options on the same term.
 */
const search = ref<string | undefined>(undefined)

const applySearch = useDebounceFn((value: string) => {
  search.value = value.trim() || undefined
}, 400)

const params = computed<AdminListLinkableSalesDemosParams>(() => ({ search: search.value }))

const { data, isPending } = useAdminListLinkableSalesDemos(params)

const demos = computed<ListLinkableSalesDemosLinkableDemoDto[]>(() => data.value?.demos ?? [])

const formatDate = (d: Date | string | null | undefined) => {
  if (!d) return null

  return new Intl.DateTimeFormat('nl-NL', { dateStyle: 'short' }).format(new Date(d))
}

function subscriptionLabel(demo: ListLinkableSalesDemosLinkableDemoDto): string | null {
  if (!demo.potentialSubscriptionType) return null
  if (demo.potentialSubscriptionType === 'MatchCredits') {
    return `${demo.potentialMatchCredits ?? 0} match credits`
  }

  return demo.potentialSubscriptionTitle ?? 'Vacature Slots'
}

const selected = computed({
  get: () => props.modelValue,
  set: v => emit('update:modelValue', v),
})
</script>

<template>
  <Select
    v-model="selected"
    :input-id="inputId"
    :options="demos"
    option-label="companyName"
    option-value="id"
    :invalid="invalid"
    :loading="isPending"
    :disabled="disabled"
    :placeholder="placeholder"
    :show-clear="showClear"
    filter
    :filter-fields="['companyName', 'salesPersonName']"
    filter-placeholder="Zoek op bedrijf of sales persoon"
    class="w-full"
    @filter="applySearch($event.value)"
  >
    <template #option="{ option }">
      <div class="flex flex-col gap-0.5">
        <div class="flex items-center gap-2">
          <span class="text-sm font-medium">{{ option.companyName }}</span>
          <Tag :value="getStatusLabel(option.status)" :severity="getStatusSeverity(option.status)" />
        </div>
        <span class="text-xs text-surface-400">
          {{ option.salesPersonName }}
          <template v-if="formatDate(option.demoDate)"> — demo {{ formatDate(option.demoDate) }}</template>
          <template v-if="subscriptionLabel(option)"> — {{ subscriptionLabel(option) }}</template>
        </span>
      </div>
    </template>
    <template #empty>
      <span class="text-sm text-surface-400">Geen demo's zonder bedrijf gevonden.</span>
    </template>
  </Select>
</template>
